import * as React from "react";
import { Icon } from "../actions/Icon";

/**
 * Multi-value field that turns typed entries into removable chips: invite emails, tags.
 * Controlled. Enter, comma or leaving the field commits what was typed; Backspace in an empty
 * field removes the last chip. Pasted lists split on commas, semicolons and new lines.
 */
export interface ChipInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange"> {
  /** The committed values, in order. */
  values: string[];
  onChange: (values: string[]) => void;
  /** Visible label, associated with the text field. */
  label?: React.ReactNode;
  /** Accessible name for the text field when there is no visible label. */
  inputLabel?: string;
  /** Shown while there are no chips, e.g. "Add emails". */
  placeholder?: string;
  /** Inline node on the right of the field, e.g. a role Select. */
  trailing?: React.ReactNode;
  /** Accessible name for each chip's remove button. Default `Remove ${value}`. */
  removeLabel?: (value: string) => string;
  /** Type of the text field, e.g. "email" for the on-screen keyboard. Default "text". */
  inputType?: "text" | "email";
  disabled?: boolean;
}

export function ChipInput({
  values,
  onChange,
  label,
  inputLabel,
  placeholder,
  trailing,
  removeLabel = (v) => "Remove " + v,
  inputType = "text",
  disabled,
  className,
  ...rest
}: ChipInputProps): JSX.Element {
  const id = React.useId();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [text, setText] = React.useState("");

  const commit = (raw: string) => {
    const next = [...values];
    for (const part of raw.split(/[,;\n]/)) {
      const v = part.trim();
      if (v && !next.includes(v)) next.push(v);
    }
    if (next.length !== values.length) onChange(next);
    setText("");
  };

  const remove = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
    inputRef.current?.focus();
  };

  return (
    <div className={["gk-chipinput", disabled && "gk-chipinput--disabled", className].filter(Boolean).join(" ")} {...rest}>
      {label !== undefined && (
        <label className="gk-chipinput__label" htmlFor={id}>
          {label}
        </label>
      )}
      {/* Clicking the empty part of the box focuses the text field, as a single input would. */}
      <div
        className="gk-chipinput__box"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) {
            e.preventDefault();
            inputRef.current?.focus();
          }
        }}
      >
        {values.map((v, i) => (
          <span key={v} className="gk-chipinput__chip">
            <span className="gk-chipinput__chiptext">{v}</span>
            <button
              type="button"
              className="gk-chipinput__remove"
              aria-label={removeLabel(v)}
              disabled={disabled}
              onClick={() => remove(i)}
            >
              <Icon name="x" size={12} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type={inputType}
          className="gk-chipinput__input"
          value={text}
          disabled={disabled}
          placeholder={values.length ? undefined : placeholder}
          aria-label={label === undefined ? inputLabel : undefined}
          onChange={(e) => {
            const v = e.target.value;
            // A pasted list (or a typed comma that slipped past keydown) commits immediately.
            if (/[,;]/.test(v)) commit(v);
            else setText(v);
          }}
          onPaste={(e) => {
            // A text input drops new lines from pasted text, so split a pasted list here.
            const pasted = e.clipboardData.getData("text");
            if (/[,;\n]/.test(pasted)) {
              e.preventDefault();
              commit(text + pasted);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit(text);
            } else if (e.key === "Backspace" && text === "" && values.length) {
              e.preventDefault();
              onChange(values.slice(0, -1));
            }
          }}
          onBlur={() => {
            if (text.trim()) commit(text);
          }}
        />
        {trailing}
      </div>
    </div>
  );
}
